/**
 * ProfileStep — seller registration step 1 (after Start).
 *
 * RHF + Zod form with:
 *   - business_name (text input)
 *   - market_id (native select from GET /marketplace/markets)
 *   - pavilion_number
 *   - entrance_hint (optional)
 *   - phone_e164
 *   - whatsapp toggle → whatsapp_phone_e164 (optional, shown when toggled)
 *   - telegram_handle (optional)
 *
 * On submit → POST /sellers/register/{id}/profile → `onProfileSaved(profileData)`.
 *
 * Markets dropdown: fetched via `apiFetchMarkets` prop (testable injection).
 * The displayed market name is `name_ru` (default locale; KY falls back to RU
 * in the dropdown since we are displaying a selection list, not localizing the
 * market name itself — that's a future UX story).
 *
 * Resume hydration: the parent passes `defaultValues` from the resume response.
 */
import { useState, useEffect, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { SELLER_REG_NS } from "@/features/seller-registration/api";
import type { PublicMarketListItem } from "@/features/seller-registration/api";
import {
  profileSchema,
  profileDefaultValues,
  type ProfileFormValues,
} from "@/features/seller-registration/schemas";
import { ApiError } from "@/lib/api-client";

const STEP_H1_CLASS =
  "text-[27px] font-bold leading-tight tracking-[-0.03em] text-foreground";

export interface ProfileStepProps {
  applicationId: string;
  /** Pre-filled values from resume. */
  initialValues?: Partial<ProfileFormValues>;
  /**
   * The market slug from a resumed application. Once the markets list loads,
   * if `market_id` is still empty (because resume sets it to ""), the matching
   * market is located by slug and `market_id` is restored via setValue.
   */
  resumedMarketSlug?: string;
  /** Injected for testability. */
  apiFetchMarkets: () => Promise<{ items: PublicMarketListItem[] }>;
  apiRegisterProfile: (
    applicationId: string,
    body: {
      business_name: string;
      market_slug: string;
      pavilion_number: string;
      entrance_hint: string;
      phone_e164: string;
      whatsapp_phone_e164?: string | null;
      telegram_handle?: string | null;
    },
  ) => Promise<{ ok: boolean; next: string }>;
  onProfileSaved: (data: ProfileFormValues, marketSlug: string) => void;
  onBack: () => void;
}

export function ProfileStep({
  applicationId,
  initialValues,
  resumedMarketSlug,
  apiFetchMarkets,
  apiRegisterProfile,
  onProfileSaved,
  onBack: _onBack,
}: ProfileStepProps): ReactElement {
  const { t } = useTranslation(SELLER_REG_NS);
  const [markets, setMarkets] = useState<PublicMarketListItem[]>([]);
  const [marketsLoading, setMarketsLoading] = useState(true);
  const [showWhatsapp, setShowWhatsapp] = useState(
    !!initialValues?.whatsapp_phone_e164,
  );

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { ...profileDefaultValues, ...initialValues },
    mode: "onBlur",
  });

  const isSubmitting = form.formState.isSubmitting;
  const errors = form.formState.errors;

  // Fetch markets on mount
  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetchMarkets();
        setMarkets(data.items);
      } catch {
        /* non-fatal — user sees empty dropdown */
      } finally {
        setMarketsLoading(false);
      }
    })();
  }, [apiFetchMarkets]);

  // Restore market selection from a resumed application.
  // Runs whenever markets are loaded or resumedMarketSlug changes.
  // Only activates when market_id is still blank (avoid overwriting a user pick).
  useEffect(() => {
    if (!resumedMarketSlug || markets.length === 0) return;
    const currentMarketId = form.getValues("market_id");
    if (currentMarketId) return; // already set — don't stomp
    const match = markets.find((m) => m.slug === resumedMarketSlug);
    if (match) {
      form.setValue("market_id", match.id, { shouldValidate: false });
    }
  }, [markets, resumedMarketSlug, form]);

  const onSubmit = async (values: ProfileFormValues): Promise<void> => {
    form.clearErrors("root");
    // Derive the market slug from the selected market id
    const selectedMarket = markets.find((m) => m.id === values.market_id);
    const marketSlug = selectedMarket?.slug ?? values.market_id;

    try {
      await apiRegisterProfile(applicationId, {
        business_name: values.business_name,
        market_slug: marketSlug,
        pavilion_number: values.pavilion_number,
        entrance_hint: values.entrance_hint ?? "",
        phone_e164: values.phone_e164,
        whatsapp_phone_e164:
          showWhatsapp && values.whatsapp_phone_e164
            ? values.whatsapp_phone_e164
            : null,
        telegram_handle: values.telegram_handle || null,
      });
      onProfileSaved(values, marketSlug);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      const key = code ? `errors.${String(code)}` : "errors.generic";
      form.setError("root", { message: key });
    }
  };

  const fieldClass = "flex flex-col gap-1.5";
  const errorClass = "text-xs text-destructive";

  return (
    <div className="flex flex-1 flex-col gap-6 py-2">
      <div className="flex flex-col gap-2">
        <h1 className={STEP_H1_CLASS} data-testid="profile-h1">
          {t("profile.h1")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("profile.lede")}</p>
      </div>

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="flex flex-1 flex-col gap-4"
      >
        {/* Business name */}
        <div className={fieldClass}>
          <Label htmlFor="prof-business-name">{t("profile.businessNameLabel")}</Label>
          <Input
            id="prof-business-name"
            type="text"
            placeholder={t("profile.businessNamePlaceholder")}
            aria-required="true"
            aria-invalid={errors.business_name !== undefined || undefined}
            aria-describedby={errors.business_name ? "prof-business-name-error" : undefined}
            disabled={isSubmitting}
            data-testid="prof-business-name"
            {...form.register("business_name")}
          />
          {errors.business_name ? (
            <p id="prof-business-name-error" role="alert" className={errorClass} data-testid="prof-business-name-error">
              {t(errors.business_name.message ?? "errors.required")}
            </p>
          ) : null}
        </div>

        {/* Market dropdown */}
        <div className={fieldClass}>
          <Label htmlFor="prof-market">{t("profile.marketLabel")}</Label>
          <select
            id="prof-market"
            aria-required="true"
            aria-invalid={errors.market_id !== undefined || undefined}
            aria-describedby={errors.market_id ? "prof-market-error" : undefined}
            disabled={isSubmitting || marketsLoading}
            data-testid="prof-market"
            className={[
              "flex h-[44px] w-full items-center rounded-md border bg-background px-3",
              "text-sm text-foreground focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:opacity-50",
              errors.market_id ? "border-destructive" : "border-input",
            ].join(" ")}
            {...form.register("market_id")}
          >
            <option value="">{t("profile.marketPlaceholder")}</option>
            {markets.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name_ru}
              </option>
            ))}
          </select>
          {errors.market_id ? (
            <p id="prof-market-error" role="alert" className={errorClass} data-testid="prof-market-error">
              {t(errors.market_id.message ?? "errors.marketRequired")}
            </p>
          ) : null}
        </div>

        {/* Pavilion number */}
        <div className={fieldClass}>
          <Label htmlFor="prof-pavilion">{t("profile.pavilionLabel")}</Label>
          <Input
            id="prof-pavilion"
            type="text"
            placeholder={t("profile.pavilionPlaceholder")}
            aria-required="true"
            aria-invalid={errors.pavilion_number !== undefined || undefined}
            aria-describedby={errors.pavilion_number ? "prof-pavilion-error" : undefined}
            disabled={isSubmitting}
            data-testid="prof-pavilion"
            {...form.register("pavilion_number")}
          />
          {errors.pavilion_number ? (
            <p id="prof-pavilion-error" role="alert" className={errorClass} data-testid="prof-pavilion-error">
              {t(errors.pavilion_number.message ?? "errors.required")}
            </p>
          ) : null}
        </div>

        {/* Entrance hint (optional) */}
        <div className={fieldClass}>
          <Label htmlFor="prof-entrance">{t("profile.entranceHintLabel")}</Label>
          <Input
            id="prof-entrance"
            type="text"
            placeholder={t("profile.entranceHintPlaceholder")}
            disabled={isSubmitting}
            data-testid="prof-entrance"
            {...form.register("entrance_hint")}
          />
          {errors.entrance_hint ? (
            <p role="alert" className={errorClass}>
              {t(errors.entrance_hint.message ?? "errors.entranceMax")}
            </p>
          ) : null}
        </div>

        {/* Phone */}
        <div className={fieldClass}>
          <Label htmlFor="prof-phone">{t("profile.phoneLabel")}</Label>
          <Input
            id="prof-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={t("profile.phonePlaceholder")}
            aria-required="true"
            aria-invalid={errors.phone_e164 !== undefined || undefined}
            aria-describedby={errors.phone_e164 ? "prof-phone-error" : undefined}
            disabled={isSubmitting}
            data-testid="prof-phone"
            {...form.register("phone_e164")}
          />
          {errors.phone_e164 ? (
            <p id="prof-phone-error" role="alert" className={errorClass} data-testid="prof-phone-error">
              {t(errors.phone_e164.message ?? "errors.phoneFormat")}
            </p>
          ) : null}
        </div>

        {/* WhatsApp toggle + conditional field */}
        <div className="flex flex-col gap-2">
          <label className="flex h-[44px] cursor-pointer items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={showWhatsapp}
              onChange={(e) => {
                setShowWhatsapp(e.target.checked);
                if (!e.target.checked) {
                  void form.setValue("whatsapp_phone_e164", "");
                }
              }}
              className="h-4 w-4 rounded border-input accent-primary"
              data-testid="prof-whatsapp-toggle"
            />
            {t("profile.whatsappToggle")}
          </label>
          {showWhatsapp ? (
            <div className={fieldClass}>
              <Label htmlFor="prof-whatsapp">{t("profile.whatsappLabel")}</Label>
              <Input
                id="prof-whatsapp"
                type="tel"
                inputMode="tel"
                placeholder={t("profile.whatsappPlaceholder")}
                aria-invalid={errors.whatsapp_phone_e164 !== undefined || undefined}
                disabled={isSubmitting}
                data-testid="prof-whatsapp"
                {...form.register("whatsapp_phone_e164")}
              />
              {errors.whatsapp_phone_e164 ? (
                <p role="alert" className={errorClass}>
                  {t(errors.whatsapp_phone_e164.message ?? "errors.phoneFormat")}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Telegram (optional) */}
        <div className={fieldClass}>
          <Label htmlFor="prof-telegram">{t("profile.telegramLabel")}</Label>
          <Input
            id="prof-telegram"
            type="text"
            placeholder={t("profile.telegramPlaceholder")}
            aria-invalid={errors.telegram_handle !== undefined || undefined}
            disabled={isSubmitting}
            data-testid="prof-telegram"
            {...form.register("telegram_handle")}
          />
          {errors.telegram_handle ? (
            <p role="alert" className={errorClass}>
              {t(errors.telegram_handle.message ?? "errors.telegramFormat")}
            </p>
          ) : null}
        </div>

        {/* Root error */}
        {errors.root ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
            data-testid="prof-root-error"
          >
            <p className="text-sm text-destructive">
              {t(errors.root.message ?? "errors.generic")}
            </p>
          </div>
        ) : null}

        <div className="mt-auto flex flex-col gap-3 pt-2">
          <button
            type="submit"
            disabled={isSubmitting}
            data-testid="prof-submit"
            className={[
              "flex h-[44px] w-full items-center justify-center gap-2",
              "rounded-[var(--g-radius)] bg-primary px-4 text-sm font-semibold",
              "text-[var(--g-on-primary)] shadow-[var(--g-shadow-sm)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "focus-visible:ring-offset-2 disabled:opacity-60",
            ].join(" ")}
          >
            {isSubmitting ? (
              <Loader2 size={16} aria-hidden="true" className="animate-spin" />
            ) : null}
            {isSubmitting ? t("cta.submitting") : t("cta.continue")}
          </button>
        </div>
      </form>
    </div>
  );
}
